import React, { useEffect, useMemo, useState } from 'react';
import { aiAPI } from '../../services/api';

const LENGTH_OPTIONS = [
    { key: 'short', label: 'Kurz', hint: '~200 Wörter', desc: 'Kompakter Überblick' },
    { key: 'medium', label: 'Mittel', hint: '~350 Wörter', desc: 'Standard-Bericht' },
    { key: 'long', label: 'Lang', hint: '~450 Wörter', desc: 'Ausführlicher Spielverlauf' },
    { key: 'custom', label: 'Frei', hint: 'Eigener Richtwert', desc: 'Individuelle Länge' },
];

const EVENT_ICONS = { 2: '⚽', 3: '🟨', 4: '🟥', 5: '🔄', 6: '⏸', 7: '🏁', 8: '💥' };

function countWords(text) {
    return (text || '').trim().split(/\s+/).filter(Boolean).length;
}

function MatchMetaGrid({ data }) {
    if (!data) return null;
    const items = [
        data.dateTime && { icon: '📅', label: 'Termin', value: data.dateTime },
        data.stadium && { icon: '🏟', label: 'Spielort', value: data.stadium },
        data.referee && { icon: '🎽', label: 'Schiedsrichter', value: data.referee },
        data.spectators && { icon: '👥', label: 'Zuschauer', value: data.spectators },
    ].filter(Boolean);

    if (!items.length) return null;

    return (
        <div className="report-meta-grid">
            {items.map((item) => (
                <div key={item.label} className="report-meta-item">
                    <span className="report-meta-icon">{item.icon}</span>
                    <div>
                        <span className="report-meta-label">{item.label}</span>
                        <span className="report-meta-value">{item.value}</span>
                    </div>
                </div>
            ))}
        </div>
    );
}

function LineupBlock({ name, text }) {
    if (!text) return null;
    return (
        <div className="report-lineup-block">
            <h4 className="report-lineup-team">{name}</h4>
            <p className="report-lineup-text">{text}</p>
        </div>
    );
}

export default function ReportGenerator({ gameId, events }) {
    const [reportData, setReportData] = useState(null);
    const [report, setReport] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    const [error, setError] = useState('');
    const [copyDone, setCopyDone] = useState(false);
    const [copyDataDone, setCopyDataDone] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editText, setEditText] = useState('');
    const [length, setLength] = useState('medium');
    const [customWords, setCustomWords] = useState('500');
    const [eventsOpen, setEventsOpen] = useState(true);

    const reportEvents = useMemo(
        () => (events || [])
            .filter((e) => e.published !== false && e.reportFlags?.useInReport)
            .sort((a, b) => {
                const dm = (a.minute || 0) - (b.minute || 0);
                if (dm !== 0) return dm;
                return new Date(a.createdAt) - new Date(b.createdAt);
            }),
        [events]
    );

    const allEvents = (events || []).filter((e) => e.published !== false);

    useEffect(() => {
        if (!gameId) return;
        setLoadingData(true);
        aiAPI.getReportData(gameId)
            .then((res) => setReportData(res.data))
            .catch((err) => setError(err.response?.data?.message || 'Spieldaten konnten nicht geladen werden'))
            .finally(() => setLoadingData(false));
    }, [gameId, events]);

    const generate = async () => {
        setLoading(true);
        setError('');
        setReport('');
        setEditMode(false);
        try {
            const body = { length };
            if (length === 'custom') body.wordCount = parseInt(customWords, 10) || 350;
            const res = await aiAPI.generateReport(gameId, body);
            setReport(res.data.report || '');
            setEditText(res.data.report || '');
            if (res.data.spieldaten) {
                setReportData((prev) => ({ ...prev, spieldaten: res.data.spieldaten }));
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = (text, setter) => {
        navigator.clipboard.writeText(text).then(() => {
            setter(true);
            setTimeout(() => setter(false), 2000);
        });
    };

    const activeText = editMode ? editText : report;
    const reportWords = countWords(activeText);
    const markedPct = allEvents.length
        ? Math.round((reportEvents.length / allEvents.length) * 100)
        : 0;

    return (
        <div className="report-generator">
            <header className="report-hero">
                <div className="report-hero-main">
                    <div className="report-hero-icon">📄</div>
                    <div>
                        <h3 className="report-hero-title">KI-Spielbericht</h3>
                        <p className="report-hero-sub">
                            Markierte Ticker-Einträge werden kuratiert und kompakt zu einem Bericht —
                            weniger ist oft besser (⭐ Schlüsselszenen helfen).
                        </p>
                    </div>
                </div>
                <div className="report-hero-stats">
                    <div className="report-stat">
                        <span className="report-stat-num">{reportEvents.length}</span>
                        <span className="report-stat-label">für Bericht</span>
                    </div>
                    <div className="report-stat">
                        <span className="report-stat-num">{allEvents.length}</span>
                        <span className="report-stat-label">Events gesamt</span>
                    </div>
                    <div className="report-stat report-stat--accent">
                        <span className="report-stat-num">{markedPct}%</span>
                        <span className="report-stat-label">markiert</span>
                    </div>
                </div>
            </header>

            {reportEvents.length === 0 && (
                <div className="report-hint">
                    <span>💡</span>
                    <p>
                        Noch keine Einträge mit <strong>📝 Bericht</strong> markiert.
                        Wichtige Momente im Ticker kennzeichnen — Tore, Chancen, Karten und Wendepunkte.
                    </p>
                </div>
            )}

            <section className="report-card">
                <div className="report-card-head">
                    <h4>Spieldaten</h4>
                    {reportData?.spieldaten && (
                        <button
                            type="button"
                            className="report-btn report-btn--ghost"
                            onClick={() => handleCopy(reportData.spieldaten, setCopyDataDone)}
                        >
                            {copyDataDone ? '✓ Kopiert' : '📋 Rohdaten'}
                        </button>
                    )}
                </div>

                <div className="report-card-body">
                    {loadingData ? (
                        <div className="report-loading">
                            <span className="report-spinner" />
                            <span>Spieldaten werden geladen…</span>
                        </div>
                    ) : reportData ? (
                        <>
                            {(reportData.headerLine || reportData.homeName) && (
                                <div className="report-match-header">
                                    {reportData.headerLine && (
                                        <span className="report-competition">{reportData.headerLine}</span>
                                    )}
                                    <div className="report-scoreboard">
                                        <span className="report-team report-team--home">{reportData.homeName}</span>
                                        <span className="report-score">
                                            {reportData.score}
                                            {reportData.scoreHT && (
                                                <span className="report-score-ht">HT {reportData.scoreHT}</span>
                                            )}
                                        </span>
                                        <span className="report-team report-team--away">{reportData.awayName}</span>
                                    </div>
                                </div>
                            )}

                            <MatchMetaGrid data={reportData} />

                            {(reportData.homeLineup || reportData.awayLineup) && (
                                <div className="report-lineups">
                                    <LineupBlock name={reportData.homeName} text={reportData.homeLineup} />
                                    <LineupBlock name={reportData.awayName} text={reportData.awayLineup} />
                                </div>
                            )}

                            <div className="report-facts">
                                {reportData.goalSequence && (
                                    <div className="report-fact">
                                        <span className="report-fact-icon">⚽</span>
                                        <div>
                                            <span className="report-fact-label">Torfolge</span>
                                            <span className="report-fact-value">{reportData.goalSequence}</span>
                                        </div>
                                    </div>
                                )}
                                {reportData.yellowCards && (
                                    <div className="report-fact">
                                        <span className="report-fact-icon">🟨</span>
                                        <div>
                                            <span className="report-fact-label">Gelbe Karten</span>
                                            <span className="report-fact-value">{reportData.yellowCards}</span>
                                        </div>
                                    </div>
                                )}
                                {reportData.redCards && reportData.redCards !== 'keine' && (
                                    <div className="report-fact">
                                        <span className="report-fact-icon">🟥</span>
                                        <div>
                                            <span className="report-fact-label">Rote Karten</span>
                                            <span className="report-fact-value">{reportData.redCards}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <p className="report-empty">Keine Spieldaten — Aufstellung und Events ergänzen.</p>
                    )}
                </div>
            </section>

            {reportEvents.length > 0 && (
                <section className="report-card report-card--events">
                    <button
                        type="button"
                        className="report-card-head report-card-head--toggle"
                        onClick={() => setEventsOpen((o) => !o)}
                    >
                        <h4>⚡ Ereignisse für Bericht ({reportEvents.length})</h4>
                        <span className="report-toggle-icon">{eventsOpen ? '▾' : '▸'}</span>
                    </button>
                    {eventsOpen && (
                        <div className="report-timeline">
                            {reportEvents.map((e) => (
                                <div key={e._id} className="report-timeline-item">
                                    <span className="report-timeline-minute">
                                        {e.minute > 0 ? `${e.minute}'` : '–'}
                                    </span>
                                    <span className="report-timeline-dot">
                                        {EVENT_ICONS[e.type] || '•'}
                                    </span>
                                    <p className="report-timeline-text">
                                        {e.textPublished || e.textResolved || e.textRaw}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            )}

            <section className="report-controls">
                <h4 className="report-controls-title">Berichtslänge</h4>
                <div className="report-length-grid">
                    {LENGTH_OPTIONS.map((opt) => (
                        <button
                            key={opt.key}
                            type="button"
                            className={`report-length-card ${length === opt.key ? 'is-active' : ''}`}
                            onClick={() => setLength(opt.key)}
                        >
                            <span className="report-length-label">{opt.label}</span>
                            <span className="report-length-hint">{opt.hint}</span>
                            <span className="report-length-desc">{opt.desc}</span>
                        </button>
                    ))}
                </div>
                {length === 'custom' && (
                    <div className="report-custom-length">
                        <input
                            type="number"
                            min="80"
                            max="2000"
                            value={customWords}
                            onChange={(e) => setCustomWords(e.target.value)}
                            className="report-custom-input"
                        />
                        <span>Wörter (Richtwert)</span>
                    </div>
                )}

                <button
                    type="button"
                    onClick={generate}
                    disabled={loading}
                    className="report-generate-btn"
                >
                    {loading ? (
                        <>
                            <span className="report-spinner report-spinner--light" />
                            Bericht wird generiert…
                        </>
                    ) : (
                        <>✨ Spielbericht generieren</>
                    )}
                </button>
            </section>

            {error && (
                <div className="report-error">
                    <span>⚠️</span> {error}
                </div>
            )}

            {report && (
                <section className="report-output">
                    <div className="report-output-head">
                        <div>
                            <h4>Generierter Spielbericht</h4>
                            <span className="report-word-count">{reportWords} Wörter</span>
                        </div>
                        <div className="report-output-actions">
                            <button
                                type="button"
                                className={`report-btn ${editMode ? 'report-btn--primary' : 'report-btn--ghost'}`}
                                onClick={() => { setEditMode((m) => !m); setEditText(report); }}
                            >
                                {editMode ? '👁 Vorschau' : '✏️ Bearbeiten'}
                            </button>
                            <button
                                type="button"
                                className="report-btn report-btn--ghost"
                                onClick={() => handleCopy(activeText, setCopyDone)}
                            >
                                {copyDone ? '✓ Kopiert!' : '📋 Kopieren'}
                            </button>
                        </div>
                    </div>
                    <div className="report-output-body">
                        {editMode ? (
                            <textarea
                                className="report-textarea"
                                rows={18}
                                value={editText}
                                onChange={(e) => setEditText(e.target.value)}
                            />
                        ) : (
                            <article className="report-prose">{activeText}</article>
                        )}
                    </div>
                    <div className="report-output-foot">
                        <button
                            type="button"
                            onClick={generate}
                            disabled={loading}
                            className="report-btn report-btn--ghost"
                        >
                            🔄 Neu generieren
                        </button>
                    </div>
                </section>
            )}
        </div>
    );
}
